import cytoscape from 'cytoscape';

const PROVIDER_NODE_TYPES = new Set(['calendar', 'competition', 'form', 'product', 'apppurchase', 'settings']);
const RESTRICTION_EDGE_MARKERS = ['restrict', 'exclude', 'include', 'limit', 'block'];
const STAGE_SPACING_X_BY_MODE = {
    compact: 240,
    comfortable: 340,
    spacious: 460,
};
const TRANSITION_FEATURE_EDGE_TYPES = {
    forms_complete_move: 'group_forms_complete_move',
    time_limit_move: 'group_time_limit_move',
    time_limit_add: 'group_time_limit_add',
    coach_assigned: 'group_coach_assigned_move',
    contract_end_add: 'group_contract_end_add',
    program_end_add: 'group_program_end_add',
    remove_on_attach: 'group_remove_on_attach',
    add_on_detach: 'group_add_on_detach',
    admin_move_button: 'group_admin_move_button_target',
    admin_add_buttons: 'group_admin_add_button_targets',
    admin_dropdown: 'group_admin_dropdown_targets',
};
const TRANSITION_EDGE_TYPE_TO_FEATURE = Object.entries(TRANSITION_FEATURE_EDGE_TYPES).reduce((carry, [featureKey, edgeType]) => {
    carry[edgeType] = featureKey;
    return carry;
}, {});
const TRIGGER_FEATURE_MATCHERS = {
    purchases: (edgeType) => edgeType.indexOf('product_attach_on_order') === 0,
    form_submissions: (edgeType) => edgeType.indexOf('form_attach_on_entry') === 0,
    calendar_attendance: (edgeType) => edgeType.indexOf('calendar_attendance') === 0 || edgeType.indexOf('calendar_nonattendance') === 0,
    competitions: (edgeType) => edgeType.indexOf('competition_register') === 0 || edgeType.indexOf('competition_cancel') === 0,
    social_signup: (edgeType) => edgeType.indexOf('settings_social_signup') === 0,
    login_reminders: (edgeType) => edgeType.indexOf('settings_login_reminder') === 0,
    program_pause: (edgeType) => edgeType.indexOf('settings_program_pause') === 0 || edgeType.indexOf('settings_program_unpause') === 0,
    product_program_end: (edgeType) => edgeType.indexOf('product_program_end') === 0,
    subscription_credits: (edgeType) => edgeType.indexOf('subscriptioncredits_redeem') === 0,
};
const RESTRICTION_FEATURE_MATCHERS = {
    calendar_restrictions: (edgeType) => edgeType.indexOf('calendar_restrict_') === 0,
    competition_restrict: (edgeType) => edgeType === 'competition_restrict_group',
    login_reminder_rules: (edgeType) => edgeType === 'settings_login_reminder_include' || edgeType === 'settings_login_reminder_exclude',
    group_limits: (edgeType) => edgeType === 'group_limit_block',
};

function isStageNodeId(nodeId) {
    return typeof nodeId === 'string' && nodeId.indexOf('stage-') === 0;
}

function isGroupNodeId(nodeId) {
    return typeof nodeId === 'string' && nodeId.indexOf('group-') === 0;
}

function classifyNodeKind(type) {
    if (type === 'stage') {
        return 'stage';
    }
    if (type === 'group') {
        return 'group';
    }
    if (PROVIDER_NODE_TYPES.has(type)) {
        return 'provider';
    }
    return 'content';
}

function classifyEdgeKind(sourceId, targetId, type) {
    const edgeType = (type || '').toString().toLowerCase();
    const targetIsProgressionNode = isStageNodeId(targetId) || isGroupNodeId(targetId);

    if (edgeType.indexOf('group_') === 0 && targetIsProgressionNode) {
        return 'transition';
    }

    if (isStageNodeId(sourceId) && targetIsProgressionNode) {
        return 'transition';
    }

    if (!isStageNodeId(sourceId) && targetIsProgressionNode) {
        const isRestriction = RESTRICTION_EDGE_MARKERS.some((marker) => edgeType.indexOf(marker) !== -1);
        return isRestriction ? 'restriction' : 'trigger';
    }

    return 'content';
}

function withComputedKinds(graphData) {
    const rawNodes = Array.isArray(graphData?.nodes) ? graphData.nodes : [];
    const rawEdges = Array.isArray(graphData?.edges) ? graphData.edges : [];

    const nodes = rawNodes.map((node) => {
        const data = { ...(node?.data || {}) };
        data.kind = classifyNodeKind(data.type);
        return { ...node, data };
    });

    const edges = rawEdges.map((edge) => {
        const data = { ...(edge?.data || {}) };
        data.kind = classifyEdgeKind(data.source, data.target, data.type);
        return { ...edge, data };
    });

    return { nodes, edges };
}

function toElements(graphData) {
    const computed = withComputedKinds(graphData);
    const nodes = Array.isArray(computed.nodes) ? computed.nodes : [];
    const edges = Array.isArray(computed.edges) ? computed.edges : [];
    return [...nodes, ...edges];
}

function defaultStyle() {
    return [
        {
            selector: 'node',
            style: {
                'label': '',
                'text-wrap': 'wrap',
                'text-max-width': 140,
                'font-size': 10,
                'text-valign': 'center',
                'text-halign': 'center',
                'background-color': '#e2e8f0',
                'border-color': '#94a3b8',
                'border-width': 1,
                'color': '#0f172a',
                'padding': '6px',
            }
        },
        {
            selector: 'node[type = "stage"], node[type = "group"]',
            style: {
                'label': 'data(label)',
            }
        },
        {
            selector: 'node[type = "stage"]',
            style: {
                'shape': 'round-rectangle',
                'width': 190,
                'height': 56,
                'background-color': '#bae6fd',
                'border-color': '#0284c7',
                'border-width': 2,
            }
        },
        {
            selector: 'node[external = true]',
            style: {
                'opacity': 0.85,
            }
        },
        {
            selector: 'edge',
            style: {
                'curve-style': 'bezier',
                'target-arrow-shape': 'triangle',
                'arrow-scale': 0.8,
                'line-color': '#94a3b8',
                'target-arrow-color': '#94a3b8',
                'width': 1.5,
                'label': '',
                'font-size': 9,
                'text-rotation': 'autorotate',
                'text-margin-y': -8,
                'color': '#334155',
            }
        },
        {
            selector: 'edge[kind = "transition"], edge[kind = "trigger"], edge[kind = "restriction"]',
            style: {
                'label': 'data(label)',
            }
        },
        {
            selector: 'edge:selected',
            style: {
                'label': 'data(label)',
                'line-color': '#0ea5e9',
                'target-arrow-color': '#0ea5e9',
                'width': 2.5,
            }
        },
        {
            selector: 'node:selected',
            style: {
                'border-color': '#0ea5e9',
                'border-width': 3,
            }
        },
        {
            selector: '.hidden-by-filter',
            style: {
                'display': 'none'
            }
        },
        {
            selector: '.dimmed',
            style: {
                'opacity': 0.15
            }
        },
        {
            selector: '.highlighted',
            style: {
                'opacity': 1
            }
        }
    ];
}

function defaultLayout() {
    return {
        name: 'cose',
        animate: false,
        padding: 30,
        nodeRepulsion: 9000,
        idealEdgeLength: 120,
        edgeElasticity: 0.2,
    };
}

function toNumber(value, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function average(values, fallback) {
    if (!values.length) {
        return fallback;
    }

    const sum = values.reduce((total, value) => total + value, 0);
    return sum / values.length;
}

function sortStageNodes(nodes) {
    return [...nodes].sort((a, b) => {
        const sortA = toNumber(a.data('sort_order'), Number.MAX_SAFE_INTEGER);
        const sortB = toNumber(b.data('sort_order'), Number.MAX_SAFE_INTEGER);
        if (sortA !== sortB) {
            return sortA - sortB;
        }

        return a.id().localeCompare(b.id());
    });
}

function neighboringNodeXs(node, positions, excludeNodeId = null) {
    const xs = [];
    node.neighborhood('node').forEach((neighbor) => {
        if (!neighbor || !neighbor.isNode()) {
            return;
        }
        if (excludeNodeId && neighbor.id() === excludeNodeId) {
            return;
        }

        const known = positions.get(neighbor.id());
        if (known && Number.isFinite(known.x)) {
            xs.push(known.x);
            return;
        }

        const existingX = toNumber(neighbor.position('x'), NaN);
        if (Number.isFinite(existingX)) {
            xs.push(existingX);
        }
    });
    return xs;
}

function clampZoom(cy, zoomLevel) {
    const minZoom = toNumber(cy.minZoom(), 0.2);
    const maxZoom = toNumber(cy.maxZoom(), 2.2);
    return Math.max(minZoom, Math.min(maxZoom, zoomLevel));
}

function fitAll(cy) {
    if (!cy || cy.destroyed()) return;
    cy.fit(cy.elements(), 40);
}

function fitStages(cy) {
    if (!cy || cy.destroyed()) return;

    const stageNodes = cy.nodes('[type="stage"]');
    if (!stageNodes.length) {
        fitAll(cy);
        return;
    }

    cy.fit(stageNodes, 80);
}

function zoomBy(cy, delta) {
    if (!cy || cy.destroyed()) return;

    const nextZoom = clampZoom(cy, cy.zoom() + delta);
    cy.zoom({
        level: nextZoom,
        renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 },
    });
}

function zoomIn(cy) {
    zoomBy(cy, 0.15);
}

function zoomOut(cy) {
    zoomBy(cy, -0.15);
}

function setReadableStageViewport(cy) {
    if (!cy || cy.destroyed()) return;

    const stageNodes = cy.nodes('[type="stage"]');
    const stageCount = stageNodes.length;
    if (!stageCount) {
        fitAll(cy);
        return;
    }

    cy.fit(stageNodes, 80);
    const fitZoom = cy.zoom();

    let minReadableZoom = null;
    if (stageCount <= 12) {
        minReadableZoom = 0.85;
    } else if (stageCount <= 25) {
        minReadableZoom = 0.65;
    } else if (stageCount <= 50) {
        minReadableZoom = 0.45;
    }

    const desiredZoom = clampZoom(
        cy,
        minReadableZoom === null ? fitZoom : Math.max(fitZoom, minReadableZoom)
    );

    if (desiredZoom > fitZoom) {
        const sortedStages = sortStageNodes(stageNodes.toArray());
        const firstStage = sortedStages.length ? sortedStages[0] : stageNodes.first();
        cy.zoom(desiredZoom);
        if (firstStage) {
            cy.center(firstStage);
        }
        return;
    }

    if (desiredZoom !== fitZoom) {
        cy.zoom(desiredZoom);
    }
}

function normalizeFeatureSet(featureMap) {
    if (!featureMap || typeof featureMap !== 'object' || Array.isArray(featureMap)) {
        return null;
    }

    const keys = Object.keys(featureMap);
    if (!keys.length) {
        return null;
    }

    const enabled = new Set();
    keys.forEach((key) => {
        if (featureMap[key]) {
            enabled.add(key);
        }
    });

    return enabled;
}

function edgeMatchesTransitionFeatures(edgeType, enabledTransitionFeatures) {
    if (!enabledTransitionFeatures) {
        return true;
    }
    if (!enabledTransitionFeatures.size) {
        return false;
    }

    const featureKey = TRANSITION_EDGE_TYPE_TO_FEATURE[edgeType];
    if (!featureKey) {
        return false;
    }

    return enabledTransitionFeatures.has(featureKey);
}

function edgeMatchesFeatureFamily(edgeType, enabledFeatures, matchers) {
    if (!enabledFeatures) {
        return true;
    }
    if (!enabledFeatures.size) {
        return false;
    }

    for (const featureKey of enabledFeatures) {
        const matcher = matchers[featureKey];
        if (typeof matcher === 'function' && matcher(edgeType)) {
            return true;
        }
    }

    return false;
}

function edgeMatchesContentTypeFilter(edge, enabledContentNodeTypes) {
    if (!enabledContentNodeTypes) {
        return true;
    }
    if (!enabledContentNodeTypes.size) {
        return false;
    }

    const targetNodeType = (edge.target().data('type') || '').toString().toLowerCase();
    return enabledContentNodeTypes.has(targetNodeType);
}

function collectVisibleEdgeNodeIds(cy) {
    const visibleNodeIds = new Set();
    cy.edges(':visible').forEach((edge) => {
        visibleNodeIds.add(edge.source().id());
        visibleNodeIds.add(edge.target().id());
    });
    return visibleNodeIds;
}

function applyNodeVisibilityFromRules(cy, {
    showExternalGroups,
    showProviderNodes,
    showContentNodes,
    visibleEdgeNodeIds,
}) {
    cy.nodes().forEach((node) => {
        const nodeType = (node.data('type') || '').toString().toLowerCase();
        const nodeKind = (node.data('kind') || 'content').toString();
        const isStageNode = nodeType === 'stage';
        const isExternalGroup = nodeType === 'group' && node.data('external') === true;

        if (isStageNode) {
            node.removeClass('hidden-by-filter');
            return;
        }

        if (isExternalGroup && !showExternalGroups) {
            node.addClass('hidden-by-filter');
            return;
        }

        if (nodeKind === 'provider' && !showProviderNodes) {
            node.addClass('hidden-by-filter');
            return;
        }

        if (nodeKind === 'content' && !showContentNodes) {
            node.addClass('hidden-by-filter');
            return;
        }

        if (!visibleEdgeNodeIds.has(node.id())) {
            node.addClass('hidden-by-filter');
            return;
        }

        node.removeClass('hidden-by-filter');
    });
}

function hideEdgesConnectedToHiddenNodes(cy) {
    const hiddenNodeIds = new Set();
    cy.nodes('.hidden-by-filter').forEach((node) => {
        hiddenNodeIds.add(node.id());
    });

    cy.edges().forEach((edge) => {
        if (hiddenNodeIds.has(edge.source().id()) || hiddenNodeIds.has(edge.target().id())) {
            edge.addClass('hidden-by-filter');
        }
    });
}

function layoutStageFlow(cy, options = {}) {
    if (!cy || cy.destroyed()) return;

    const spacingMode = (options && options.spacing ? options.spacing : 'comfortable').toString().toLowerCase();
    const stageSpacingX = STAGE_SPACING_X_BY_MODE[spacingMode] || STAGE_SPACING_X_BY_MODE.comfortable;
    const positions = new Map();
    const stageNodes = sortStageNodes(cy.nodes('node[type = "stage"]'));
    const stageXById = new Map();

    stageNodes.forEach((node, index) => {
        const x = index * stageSpacingX;
        const y = 0;
        positions.set(node.id(), { x, y });
        stageXById.set(node.id(), x);
    });

    const fallbackStageX = stageNodes.length ? average(stageNodes.map((node) => stageXById.get(node.id())), 0) : 0;

    cy.nodes('node[kind = "provider"]').forEach((node) => {
        const xs = [];
        node.connectedEdges().forEach((edge) => {
            if (edge.source().id() === node.id() && edge.target().data('kind') === 'stage') {
                const targetX = stageXById.get(edge.target().id());
                if (Number.isFinite(targetX)) {
                    xs.push(targetX);
                }
            }
        });

        if (!xs.length) {
            xs.push(...neighboringNodeXs(node, positions));
        }

        positions.set(node.id(), {
            x: average(xs, fallbackStageX),
            y: -220,
        });
    });

    cy.nodes('node[kind = "content"]').forEach((node) => {
        const xs = [];
        node.connectedEdges().forEach((edge) => {
            if (edge.target().id() === node.id() && edge.source().data('kind') === 'stage') {
                const sourceX = stageXById.get(edge.source().id());
                if (Number.isFinite(sourceX)) {
                    xs.push(sourceX);
                }
            }
        });

        if (!xs.length) {
            xs.push(...neighboringNodeXs(node, positions));
        }

        positions.set(node.id(), {
            x: average(xs, fallbackStageX),
            y: 220,
        });
    });

    const usedGroupXSlots = {};
    cy.nodes('node[type = "group"][external = true]').forEach((node) => {
        const xs = neighboringNodeXs(node, positions);
        const baseX = average(xs, fallbackStageX);
        const slot = Math.round(baseX / 40) * 40;
        const slotCount = usedGroupXSlots[slot] || 0;
        usedGroupXSlots[slot] = slotCount + 1;

        let y = 0;
        if (slotCount > 0) {
            const band = Math.ceil(slotCount / 2);
            y = (slotCount % 2 === 0 ? -1 : 1) * band * 24;
        }

        positions.set(node.id(), { x: baseX, y });
    });

    cy.nodes().forEach((node) => {
        const position = positions.get(node.id());
        if (position) {
            node.position(position);
        }
    });

    cy.layout({ name: 'preset', padding: 30, animate: false }).run();
}

function destroyIfExists(cy) {
    try {
        if (cy && !cy.destroyed()) {
            cy.destroy();
        }
    } catch (e) {
        // ignore
    }
}

function create(containerEl, graphData, options = {}) {
    const cy = cytoscape({
        container: containerEl,
        elements: toElements(graphData),
        style: options.style || defaultStyle(),
        layout: options.layout || defaultLayout(),
        wheelSensitivity: 0.18,
        minZoom: 0.2,
        maxZoom: 2.2,
    });

    return cy;
}

function update(cy, graphData, options = {}) {
    if (!cy || cy.destroyed()) return null;

    cy.elements().remove();
    cy.add(toElements(graphData));

    cy.layout(options.layout || defaultLayout()).run();
    return cy;
}

function applyVisibility(cy, filters = {}) {
    if (!cy || cy.destroyed()) return;

    const hasFilter = (key) => Object.prototype.hasOwnProperty.call(filters, key);
    const resolveBoolean = (key, fallback) => (hasFilter(key) ? filters[key] !== false : fallback);

    let showExternalGroups = resolveBoolean('showExternalGroups', true);
    if (hasFilter('showExternal')) {
        showExternalGroups = filters.showExternal !== false;
    }

    let showProviderNodes = resolveBoolean('showProviderNodes', true);
    let showContentNodes = resolveBoolean('showContentNodes', false);

    if (hasFilter('showEntities')) {
        const showEntities = filters.showEntities !== false;
        if (!hasFilter('showProviderNodes')) {
            showProviderNodes = showEntities;
        }
        if (!hasFilter('showContentNodes')) {
            showContentNodes = showEntities;
        }
    }

    const showTransitions = resolveBoolean('showTransitions', true);
    const showTriggers = resolveBoolean('showTriggers', true);
    const showRestrictions = resolveBoolean('showRestrictions', true);
    const showContentEdges = resolveBoolean('showContentEdges', false);
    const enabledTransitionFeatures = normalizeFeatureSet(filters.transitionFeatures);
    const enabledTriggerFeatures = normalizeFeatureSet(filters.triggerFeatures);
    const enabledRestrictionFeatures = normalizeFeatureSet(filters.restrictionFeatures);
    const enabledContentNodeTypes = normalizeFeatureSet(filters.contentNodeTypes);

    cy.elements().removeClass('hidden-by-filter');

    cy.edges().forEach((edge) => {
        const edgeKind = (edge.data('kind') || 'content').toString();
        const edgeType = (edge.data('type') || '').toString().toLowerCase();
        let showEdge = true;

        if (edgeKind === 'transition') {
            showEdge = showTransitions && edgeMatchesTransitionFeatures(edgeType, enabledTransitionFeatures);
        } else if (edgeKind === 'trigger') {
            showEdge = showTriggers && edgeMatchesFeatureFamily(edgeType, enabledTriggerFeatures, TRIGGER_FEATURE_MATCHERS);
        } else if (edgeKind === 'restriction') {
            showEdge = showRestrictions && edgeMatchesFeatureFamily(edgeType, enabledRestrictionFeatures, RESTRICTION_FEATURE_MATCHERS);
        } else {
            showEdge = showContentEdges && edgeMatchesContentTypeFilter(edge, enabledContentNodeTypes);
        }

        if (!showEdge) {
            edge.addClass('hidden-by-filter');
            return;
        }

        edge.removeClass('hidden-by-filter');
    });

    applyNodeVisibilityFromRules(cy, {
        showExternalGroups,
        showProviderNodes,
        showContentNodes,
        visibleEdgeNodeIds: collectVisibleEdgeNodeIds(cy),
    });

    hideEdgesConnectedToHiddenNodes(cy);

    applyNodeVisibilityFromRules(cy, {
        showExternalGroups,
        showProviderNodes,
        showContentNodes,
        visibleEdgeNodeIds: collectVisibleEdgeNodeIds(cy),
    });

    hideEdgesConnectedToHiddenNodes(cy);
}

function layoutCose(cy) {
    if (!cy || cy.destroyed()) return;

    cy.layout(defaultLayout()).run();
    fitAll(cy);
}

function relayout(cy, mode = 'flow', options = {}) {
    if (mode === 'full') {
        layoutCose(cy);
        return;
    }

    layoutStageFlow(cy, options);
}

function clearFocus(cy) {
    if (!cy || cy.destroyed()) return;
    cy.elements().removeClass('dimmed').removeClass('highlighted');
}

function focusOnNode(cy, nodeId, hops = 1) {
    if (!cy || cy.destroyed()) return;

    const center = cy.getElementById(nodeId);
    if (!center || center.empty()) return;

    const neighborhood = center.closedNeighborhood();
    let current = neighborhood;

    for (let i = 0; i < Math.max(0, hops - 1); i++) {
        current = current.union(current.neighborhood());
    }

    cy.elements().addClass('dimmed').removeClass('highlighted');
    current.removeClass('dimmed').addClass('highlighted');

    cy.animate({
        center: { eles: center },
        duration: 200
    });
}

window.PonoAutomationMap = {
    create,
    update,
    applyVisibility,
    fitAll,
    fitStages,
    zoomIn,
    zoomOut,
    setReadableStageViewport,
    layoutStageFlow,
    layoutCose,
    relayout,
    focusOnNode,
    clearFocus,
    destroyIfExists,
};

export default window.PonoAutomationMap;
